/**
 * RNG — Seeded deterministic pseudo-random number generator (Mulberry32).
 * All game randomness should flow through a seeded RNG instance so that
 * world generation, combat, and encounters are reproducible from a seed.
 *
 * @module RNG
 *
 * @example
 * import { createRNG, setGlobalSeed, globalRNG } from './RNG.js';
 *
 * // Per-system RNG
 * const combatRNG = createRNG(worldSeed ^ 0xDEADBEEF);
 * const roll = combatRNG.nextInt(1, 20);
 *
 * // Global fallback
 * setGlobalSeed(Date.now());
 * const name = globalRNG.pick(['Bjorn', 'Freya', 'Erik']);
 */

/**
 * Create a seeded RNG using the Mulberry32 algorithm.
 * Produces high-quality pseudo-random numbers from a 32-bit seed.
 *
 * @param {number} seed — 32-bit integer seed
 * @returns {{
 *   next(): number,
 *   nextFloat(): number,
 *   nextInt(min: number, max: number): number,
 *   nextBool(chance?: number): boolean,
 *   pick<T>(arr: T[]): T,
 *   shuffle<T>(arr: T[]): T[]
 * }}
 */
export function createRNG(seed) {
  let s = seed >>> 0;

  return {
    /**
     * Return next 32-bit unsigned integer.
     * @returns {number} Integer in range [0, 0xFFFFFFFF]
     */
    next() {
      s += 0x6D2B79F5 >>> 0;
      let z = s;
      z = Math.imul(z ^ (z >>> 15), z | 1);
      z ^= z + Math.imul(z ^ (z >>> 7), z | 61);
      return ((z ^ (z >>> 14)) >>> 0);
    },

    /**
     * Return a float in range [0, 1).
     * @returns {number}
     */
    nextFloat() {
      return this.next() / 0x100000000;
    },

    /**
     * Return an integer in the inclusive range [min, max].
     * @param {number} min
     * @param {number} max
     * @returns {number}
     */
    nextInt(min, max) {
      return min + Math.floor(this.nextFloat() * (max - min + 1));
    },

    /**
     * Return true with the given probability.
     * @param {number} [chance=0.5] — Probability between 0 and 1
     * @returns {boolean}
     */
    nextBool(chance = 0.5) {
      return this.nextFloat() < chance;
    },

    /**
     * Pick a random element from an array.
     * @template T
     * @param {T[]} arr
     * @returns {T}
     */
    pick(arr) {
      if (arr.length === 0) return undefined;
      return arr[this.nextInt(0, arr.length - 1)];
    },

    /**
     * Shuffle an array in place using Fisher-Yates and return it.
     * @template T
     * @param {T[]} arr
     * @returns {T[]}
     */
    shuffle(arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = this.nextInt(0, i);
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
      }
      return arr;
    },

    /**
     * Create a new RNG derived from this one using the next value as a seed.
     * Useful for creating independent sub-RNGs without passing seeds around.
     * @returns {ReturnType<createRNG>}
     */
    fork() {
      return createRNG(this.next());
    },

    /**
     * Return the current internal state (for save/load).
     * @returns {number}
     */
    getState() {
      return s;
    },

    /**
     * Restore internal state (for save/load).
     * @param {number} state
     */
    setState(state) {
      s = state >>> 0;
    },
  };
}

/**
 * Global shared RNG instance. Seeded with Date.now() at startup.
 * Re-seed with setGlobalSeed() when a world is loaded.
 * @type {ReturnType<createRNG>}
 */
export let globalRNG = createRNG(Date.now());

/**
 * Replace the global RNG with a freshly seeded instance.
 * Call this when a world seed is established (new game or load game).
 * @param {number} seed
 */
export function setGlobalSeed(seed) {
  globalRNG = createRNG(seed);
}

// ─── Standalone helpers (for systems that receive an RNG instance) ────────────

/**
 * Roll a d20 using an RNG instance.
 * @param {ReturnType<createRNG>} rng
 * @returns {number} Integer in [1, 20]
 */
export function rollD20(rng) {
  return rng.nextInt(1, 20);
}

/**
 * Roll an inclusive integer range using an RNG instance.
 * @param {ReturnType<createRNG>} rng
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function rollRange(rng, min, max) {
  return rng.nextInt(min, max);
}

/**
 * Pick a random element from an array using an RNG instance.
 * @template T
 * @param {ReturnType<createRNG>} rng
 * @param {T[]} arr
 * @returns {T}
 */
export function pickRandom(rng, arr) {
  return rng.pick(arr);
}
